import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { IndustryLinkPlanSection } from "@/components/industry-link-plan";
import { RelatedArticlesModule } from "@/components/related-articles-module";
import { relatedArticlesForIndustry } from "@/lib/related-articles";
import { industryLinkPlan } from "@/lib/internal-links";
import { PageHero } from "@/components/page-hero";
import { ArrowRight } from "lucide-react";
import { getIndustry, industries, type Industry } from "@/content/industries";
import { getService, services } from "@/content/services";
import { CTASection } from "@/components/cta-section";
import { IndustryLeadForm } from "@/components/industry-lead-form";
import { SeoFAQ } from "@/components/seo-faq";
import { AccreditationsStrip } from "@/components/accreditations-strip";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { ReviewStamp } from "@/components/review-stamp";
import { RelatedContent } from "@/components/related-content";
import { getRelatedContent } from "@/lib/related";
import {
  buildHead,
  generatePageTitle,
  serviceSchema,
  breadcrumbSchema,
  ensureFAQs,
  webPageSchema,
} from "@/lib/seo";

const pkFor = (industry: Industry) =>
  industry.primaryKeyword ?? `hr support for ${industry.shortName.toLowerCase()}`;

const INDUSTRY_H1: Record<string, { h1: string; lede: string }> = {
  hospitality: {
    h1: "HR for Hospitality Businesses and Employers",
    lede: "Specialist HR for hospitality employers, operators and businesses across the UK.",
  },
  construction: {
    h1: "HR for Construction Companies and Employers",
    lede: "Site-ready HR for construction employers, contractors and trades businesses.",
  },
  healthcare: {
    h1: "HR for Healthcare Providers and Employers",
    lede: "CQC-aware HR for healthcare employers, providers and care businesses UK-wide.",
  },
  "professional-services": {
    h1: "HR for Professional Services Firms and Employers",
    lede: "Discreet HR for professional services employers, partnerships and consultancy businesses.",
  },
  "retail-ecommerce": {
    h1: "HR for Retail and Ecommerce Businesses",
    lede: "Practical HR for retail and ecommerce employers, multi-site businesses and online operators.",
  },
  manufacturing: {
    h1: "HR for Manufacturing Companies and Employers",
    lede: "Shop-floor-ready HR for manufacturing employers and industrial businesses across the UK.",
  },
};

export const Route = createFileRoute("/hr-by-industry/$slug")({
  loader: ({ params }) => {
    const industry = getIndustry(params.slug);
    if (!industry) throw notFound();
    return { industry };
  },
  head: ({ loaderData }) => {
    if (!loaderData) return {};
    const { industry } = loaderData;
    const pk = pkFor(industry);
    const path = `/hr-by-industry/${industry.slug}`;
    const title = industry.seoTitle ?? generatePageTitle(industry.shortName, "industry");
    const faqs = ensureFAQs(pk, industry.faqs, "industry");
    const crumbs = [
      { label: "Home", path: "/" },
      { label: "Industries", path: "/hr-by-industry" },
      { label: industry.shortName, path },
    ];
    return buildHead({
      title,
      description: industry.metaDescription,
      path,
      keywords: industry.keywords,
      schemas: [
        serviceSchema({
          name: `HR & Employment Law for ${industry.name}`,
          description: industry.metaDescription,
          serviceType: industry.name,
        }),
        webPageSchema({
          path,
          name: title,
          description: industry.metaDescription,
          faqs,
          hasBreadcrumb: true,
          lastReviewed: industry.lastReviewed,
        }),
        // FAQ JSON-LD is nested inside the WebPage node above.
        breadcrumbSchema(crumbs, path),
      ],
    });
  },
  component: IndustryPage,
  notFoundComponent: () => (
    <div className="px-6 py-24 text-center">
      <h1 className="font-serif text-4xl text-brand">Industry not found</h1>
      <Link to="/hr-by-industry" className="mt-6 inline-block text-accent">
        Back to industries
      </Link>
    </div>
  ),
});

function IndustryPage() {
  const { industry } = Route.useLoaderData() as { industry: Industry };
  const recommended = industry.recommendedServices
    .map((slug) => getService(slug))
    .filter((s): s is NonNullable<ReturnType<typeof getService>> => Boolean(s));
  const otherIndustries = industries.filter((i) => i.slug !== industry.slug).slice(0, 3);
  const pk = pkFor(industry);
  const linkPlan = industryLinkPlan(industry.slug, industry.recommendedServices);
  // The keyword/intent-matched module owns article links, so the curated plan
  // below keeps services, calculators and templates only (no duplicate links).
  const relatedArticles = relatedArticlesForIndustry(industry, { limit: 4 });
  const planWithoutArticles = { ...linkPlan, articles: [] };

  return (
    <>
      <PageHero
        breadcrumbSchema={false}
        breadcrumbs={[
          { label: "Home", path: "/" },
          { label: "Industries", path: "/hr-by-industry" },
          { label: industry.shortName, path: `/hr-by-industry/${industry.slug}` },
        ]}
        eyebrow="Industries"
        title={industry.h1 ?? INDUSTRY_H1[industry.slug]?.h1 ?? industry.heroHeadline}
        subtitle={
          INDUSTRY_H1[industry.slug]?.lede ?? `${industry.primaryKeyword} for UK employers and businesses.`
        }
        intro={industry.heroSubheadline}
        lastReviewed={industry.lastReviewed}
        author={industry.author}
        actions={
          <>
            <Link
              to="/contact-hr-experts"
              className="inline-flex items-center gap-2 rounded-md bg-brand px-5 py-3 text-sm font-medium text-brand-foreground hover:brightness-110"
            >
              Talk to a sector specialist
              <ArrowRight className="size-4" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center rounded-md border border-border bg-card px-5 py-3 text-sm font-medium text-foreground hover:bg-secondary"
            >
              All services
            </Link>
          </>
        }
        aside={
          <div className="relative rounded-md border border-border bg-card p-8 shadow-sm">
            <span aria-hidden="true" className="absolute inset-y-0 left-0 w-1 rounded-l-md bg-accent" />
            <p className="text-[10px] font-bold uppercase tracking-[0.2em] text-accent">Case snapshot</p>
            <h3 className="mt-4 font-serif text-xl leading-tight text-brand">
              {industry.caseSnapshot.headline}
            </h3>
            <p className="mt-4 text-sm leading-relaxed text-muted-foreground">
              {industry.caseSnapshot.detail}
            </p>
          </div>
        }
      />

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <p className="text-lg leading-relaxed text-foreground/90">{industry.overview}</p>
        </div>
      </section>

      {industry.sections?.length ? (
        <section className="px-6 pb-4 lg:px-8">
          <div className="mx-auto max-w-3xl space-y-16">
            {industry.sections.map((sec) => (
              <article key={sec.heading}>
                <h2 className="font-serif text-2xl leading-tight text-brand sm:text-3xl">
                  {sec.heading}
                </h2>
                <div className="mt-5 space-y-4">
                  {sec.paragraphs.map((para) => (
                    <p key={para.slice(0, 40)} className="leading-relaxed text-muted-foreground">
                      {para}
                    </p>
                  ))}
                </div>
                {sec.bullets?.length ? (
                  <ul className="mt-6 space-y-3 border-l-2 border-accent/40 pl-6">
                    {sec.bullets.map((b) => (
                      <li key={b} className="text-sm leading-relaxed text-foreground/90">
                        {b}
                      </li>
                    ))}
                  </ul>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      ) : null}


      <RelatedArticlesModule articles={relatedArticles} sector={industry.shortName} />

      <IndustryLinkPlanSection plan={planWithoutArticles} sector={industry.shortName} />

      <section className="bg-card/40 px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-serif text-3xl text-brand">
            {industry.shortName} HR challenges we solve for employers
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            The recurring people problems {industry.shortName.toLowerCase()} businesses bring to us, and how we fix them.
          </p>
          <div className="mt-12 grid grid-cols-1 gap-6 sm:grid-cols-2">
            {industry.challenges.map((c) => (
              <div key={c.title} className="rounded-2xl bg-card p-8 ring-1 ring-black/5">
                <h3 className="font-serif text-lg text-brand">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{c.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-serif text-3xl text-brand">
            Recommended HR services for {industry.shortName.toLowerCase()} employers
          </h2>
          <p className="mt-4 max-w-2xl text-muted-foreground">
            The services we most commonly deploy for {industry.shortName.toLowerCase()} businesses and employers across the UK.
          </p>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {recommended.map((s) => (
              <Link
                key={s.slug}
                to="/services/$slug"
                params={{ slug: s.slug }}
                className="group flex flex-col rounded-2xl bg-card p-6 ring-1 ring-black/5 transition hover:ring-accent/30"
              >
                <h3 className="font-serif text-lg text-brand">{s.shortName}</h3>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">{s.summary}</p>
                <span className="mt-6 inline-flex items-center gap-1 text-sm font-medium text-accent">
                  Explore {s.shortName}
                  <ArrowRight className="size-4 transition-transform group-hover:translate-x-0.5" />
                </span>
              </Link>
            ))}
          </div>
          {recommended.length === 0 && (
            <p className="mt-6 text-sm text-muted-foreground">
              <Link to="/services" className="text-accent">
                Browse all services
              </Link>{" "}
              ({services.length} available).
            </p>
          )}
        </div>
      </section>

      <AccreditationsStrip />
      <SeoFAQ primaryKeyword={pk} faqs={industry.faqs} type="industry" />

      <RelatedContent
        heading={`More for ${pk}`}
        items={getRelatedContent({
          type: "industry",
          slug: industry.slug,
          primaryKeyword: pk,
          topics: [industry.shortName],
          diversify: true,
        })}
      />

      <section className="px-6 py-20 lg:px-8">
        <div className="mx-auto max-w-3xl">
          <IndustryLeadForm industrySlug={industry.slug} industryName={industry.shortName} />
        </div>
      </section>

      <CTASection headline={`Sector-specific HR for ${industry.shortName.toLowerCase()} businesses`} />

      <section className="px-6 pb-24 lg:px-8">
        <div className="mx-auto max-w-7xl">
          <h2 className="font-serif text-2xl text-brand">Other industries we support</h2>
          <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {otherIndustries.map((r) => (
              <Link
                key={r.slug}
                to="/hr-by-industry/$slug"
                params={{ slug: r.slug }}
                className="group rounded-2xl bg-card p-6 ring-1 ring-black/5 hover:ring-accent/30"
              >
                <h3 className="font-serif text-lg text-brand">{r.shortName}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{r.summary}</p>
                <span className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-accent">
                  HR for {r.shortName} <ArrowRight className="size-4" />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
